import { describeErasureCounts, ERASURE_REASON_LABELS, type ErasureEntry } from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Eraser } from 'lucide-react';
import { DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { api } from '../lib/api';

// Admin → Erasures (PLAN §4 `erasures`, §11; task 44): the erasure log. After a hard delete this is the only record
// of the item: what it was listed as, who erased it, why, when and how much went with it, never the content.
// Erasing itself starts from the document or entity page ("Erase permanently").

export const Route = createFileRoute('/admin/erasures')({ component: ErasuresPage });

const TOUR = {
  id: 'admin-erasures',
  steps: [
    {
      target: '[data-tour="erasures-log"]',
      title: 'The erasure log',
      body: 'Each hard delete is listed here with who did it, why and when. The content itself is gone: search, chat and the graph no longer find it.',
      learnMore: 'erase-data',
    },
  ],
};

const when = (ms: number) =>
  new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function ErasuresPage() {
  useScreenTour(TOUR);
  const q = useQuery({ queryKey: ['admin', 'erasures'], queryFn: () => api<{ erasures: ErasureEntry[] }>('/admin/erasures') });
  return (
    <div className="space-y-6">
      <PageHeader
        title="Erasures"
        description="Documents and entities erased permanently, for mistakes, legal takedowns and data-protection requests."
      />
      <Tip id="erasures-how" learnMore="erase-data">
        To erase something, open the document in the Library or the entity’s profile and use Erase permanently at the bottom. Rejecting or
        retracting keeps history; erasing deletes it everywhere.
      </Tip>
      {q.isError ? (
        <ErrorState error={q.error} title="The erasure log couldn’t be loaded" onRetry={() => q.refetch()} learnMore="erase-data" />
      ) : !q.data ? (
        <div className="h-40 animate-pulse rounded-lg bg-muted" aria-busy="true">
          <span className="sr-only">Loading…</span>
        </div>
      ) : q.data.erasures.length === 0 ? (
        <EmptyState icon={Eraser} title="Nothing has been erased" learnMore="erase-data">
          Erased documents and entities are listed here, with who erased them and why.
        </EmptyState>
      ) : (
        <section aria-labelledby="erasures-title" className="space-y-3" data-tour="erasures-log">
          <h2 id="erasures-title" className="flex items-center gap-1 font-semibold">
            Erasure log
            <FieldHelp
              label="Erasure log"
              help="Kept after the item is gone: its id, the name it is listed under, who erased it, why, when, and counts of what went with it. Backups age out within 30 days."
              learnMore="erase-data"
            />
          </h2>
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="Erasure log">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">When</th>
                  <th className="p-2 text-start font-medium">What</th>
                  <th className="p-2 text-start font-medium">Why</th>
                  <th className="p-2 text-start font-medium">Removed with it</th>
                  <th className="p-2 text-start font-medium">By</th>
                </tr>
              </thead>
              <tbody>
                {q.data.erasures.map(e => (
                  <tr key={e.id} className="border-b align-top last:border-0" data-testid="erasure-row">
                    <td className="whitespace-nowrap p-2">{when(e.createdAt)}</td>
                    <td className="max-w-xs p-2">
                      <span className="block font-medium">
                        <DataText>{e.label}</DataText>
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {e.kind === 'document' ? 'Document' : 'Entity'}
                        {e.parentId && ', with a document'} · <DataText>{e.targetId}</DataText>
                      </span>
                    </td>
                    <td className="max-w-xs p-2">
                      {ERASURE_REASON_LABELS[e.reason]}
                      {e.note && (
                        <span className="block text-xs text-muted-foreground">
                          <DataText>{e.note}</DataText>
                        </span>
                      )}
                    </td>
                    <td className="max-w-xs p-2">{describeErasureCounts(e.counts)}</td>
                    <td className="p-2">
                      <DataText>{e.erasedBy.name ?? e.erasedBy.email ?? 'Unknown'}</DataText>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <LearnMore slug="erase-data" />
    </div>
  );
}
